/**
 * Global Store Selector
 * 
 * Ensures pickup store is always selected and added to line item properties
 * when adding products to cart from any entry point (PDP, homepage, collection, etc.)
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'selectedPickupStore';
  const PROPERTY_KEY = '_pickup_store_name';
  const MODAL_TRIGGER_ID = 'ShowPickupAvailabilityDrawer';
  const MODAL_SHOWN_KEY = 'pickupModalShownOnce';
  
  // Track ongoing cart updates to prevent duplicate calls
  let cartUpdateInProgress = false;
  let lastUpdatedStore = null;

  /**
   * Get the selected pickup store from localStorage
   */
  function getSelectedStore() {
    return localStorage.getItem(STORAGE_KEY);
  }

  /**
   * Check if modal has been shown once
   */
  function hasModalBeenShown() {
    return true; // return localStorage.getItem(MODAL_SHOWN_KEY) === 'true';
  }

  /**
   * Mark modal as shown
   */
  function markModalAsShown() {
    localStorage.setItem(MODAL_SHOWN_KEY, 'true');
  }

  /**
   * Reset modal shown flag (when store is selected)
   */
  function resetModalShownFlag() {
    localStorage.removeItem(MODAL_SHOWN_KEY);
  }

  /**
   * Get the pickup store message from theme settings
   */
  function getPickupStoreMessage() {
    const defaultMessage = '<strong>Please select a store:</strong> If your order is for pickup, please select a store location below.';
    
    if (window.theme && window.theme.settings && window.theme.settings.pickupStoreMessage) {
      // Remove <p> tags if present (richtext field adds them)
      let message = window.theme.settings.pickupStoreMessage;
      message = message.replace(/^<p>/, '').replace(/<\/p>$/, '');
      return message || defaultMessage;
    }
    
    return defaultMessage;
  }

  /**
   * Load pickup store availability data for a variant
   */
  async function loadPickupStores(variantId) {
    if (!variantId) {
      console.warn('[Global Store Selector] No variant ID provided');
      return null;
    }
    
    try {
      // Fetch from the same endpoint that pickup-availability component uses
      const root = window.theme?.routes?.root || '/';
      const url = `${root}variants/${variantId}/?section_id=api-pickup-availability`;
      
      // console.log('[Global Store Selector] Fetching pickup stores from:', url);
      
      const response = await fetch(url);
      if (!response.ok) {
        console.error('[Global Store Selector] Failed to fetch pickup availability:', response.status);
        return null;
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Find the pickup list in the response
      const storeList = doc.querySelector('.pickup__list');
      if (storeList && storeList.children.length > 0) {
        return storeList.innerHTML;
      }
      
      console.warn('[Global Store Selector] No stores found in response');
      return null;
    } catch (error) {
      console.error('[Global Store Selector] Error loading pickup stores:', error);
      return null;
    }
  }

  /**
   * Get variant ID from form or cart
   */
  function getVariantIdFromContext(form) {
    // Try to get from form
    if (form) {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput && variantInput.value) {
        return variantInput.value;
      }
    }
    
    // Try to get from cart (first item)
    const cartItems = document.querySelectorAll('cart-items');
    if (cartItems.length > 0) {
      // Could get from cart, but for now return null
      // We'll load from the first cart item if needed
    }
    
    return null;
  }

  /**
   * Show the pickup availability modal
   */
  async function showPickupModal(form = null) {
    // console.log('[Global Store Selector] Attempting to show pickup modal');

    // Get variant ID from context if available
    const variantId = getVariantIdFromContext(form);

    // Get the global wrapper and dialog
    const wrapper = document.querySelector('#global-pickup-wrapper');
    const dialog = document.querySelector('#global-pickup-drawer');

    if (!wrapper || !dialog) {
      console.error('[Global Store Selector] Global pickup wrapper or dialog not found!');
      return;
    }

    // Get variant ID (from form or cart) for loading stores
    const finalVariantId = variantId || await getVariantIdFromCart();

    // Load stores for this variant
    if (finalVariantId) {
      // console.log('[Global Store Selector] Loading stores for variant:', finalVariantId);
      const storeList = dialog.querySelector('#global-pickup-store-list');
      if (storeList) {
        const storesHTML = await loadPickupStores(finalVariantId);
        if (storesHTML) {
          storeList.innerHTML = storesHTML;
        } else {
          storeList.innerHTML = '<li class="pickup__list__item"><div class="pick-up-item-content"><div class="status-pickup"><div class="text-outtock">No pickup stores available for this product</div></div></div></li>';
        }
      }
    }

    // Initialize PopupActions for the dialog if not already initialized
    // PopupActions is in theme.dev.js, try to access it from the global scope
    if (!dialog.popupActions) {
      // Try to find PopupActions from theme bundle
      const PopupActionsClass = window.PopupActions || 
                                 (window.theme && window.theme.PopupActions) ||
                                 (window.theme && window.theme.utils && window.theme.utils.PopupActions);
      
      if (PopupActionsClass) {
        try {
          dialog.popupActions = new PopupActionsClass(dialog, wrapper, true, true);
          // console.log('[Global Store Selector] PopupActions initialized');
        } catch (e) {
          console.warn('[Global Store Selector] Failed to initialize PopupActions:', e);
        }
      } else {
        console.log('[Global Store Selector] PopupActions not available, will use native dialog methods');
      }
    }

    // Open the dialog
    if (dialog.popupActions && typeof dialog.popupActions.popupOpen === 'function') {
      // console.log('[Global Store Selector] Opening dialog via PopupActions');
      dialog.popupActions.popupOpen();
    } else {
      // Fallback: open manually
      // console.log('[Global Store Selector] Opening dialog manually');
      dialog.removeAttribute('inert');
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }

      // Ensure drawer inner is visible
      const drawerInner = dialog.querySelector('.drawer__inner');
      if (drawerInner) {
        drawerInner.style.visibility = 'visible';
      }

      // Trigger scroll lock
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));
      dialog.focus();
    }

    // Set up close handlers
    setupCloseHandlers(dialog);

    // Initialize search functionality after dialog opens
    setTimeout(() => {
      const searchInput = dialog.querySelector('#pickup-search');
      const pickupItems = dialog.querySelectorAll('.pickup__list__item');
      if (searchInput && pickupItems.length > 0) {
        // console.log('[Global Store Selector] Initializing search');
        initializeSearchManually(wrapper);
      }
      
      // Set up store selection handlers
      setupStoreSelectionHandlers(wrapper);
    }, 200);

    // console.log('[Global Store Selector] Modal opened');
  }

  /**
   * Set up click handlers for store selection
   */
  function setupStoreSelectionHandlers(wrapper) {
    if (!wrapper) return;
    
    // Use event delegation to handle clicks on select buttons
    wrapper.addEventListener('click', function(e) {
      const selectButton = e.target.closest('.select-pick-up-address');
      if (selectButton) {
        e.preventDefault();
        const item = selectButton.closest('.pickup__list__item');
        if (item) {
          // console.log('[Global Store Selector] Store selection button clicked');
          handleStoreSelection(item);
        }
      }
    });
  }

  /**
   * Set up close handlers for the modal
   */
  function setupCloseHandlers(dialog) {
    if (!dialog) return;

    // Remove existing handlers to avoid duplicates
    if (dialog._closeHandlersSetup) return;
    dialog._closeHandlersSetup = true;

    // Close button handler
    const closeButtons = dialog.querySelectorAll('[data-popup-close]');
    closeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        // console.log('[Global Store Selector] Close button clicked');
        closePickupModal(dialog);
      });
    });

    // ESC key handler
    dialog.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' || e.code === 'Escape') {
        e.preventDefault();
        // console.log('[Global Store Selector] ESC key pressed');
        closePickupModal(dialog);
      }
    });

    // Click outside handler (on dialog backdrop)
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) {
        // console.log('[Global Store Selector] Clicked outside dialog');
        closePickupModal(dialog);
      }
    });

    // Native close event handler
    dialog.addEventListener('close', () => {
      // console.log('[Global Store Selector] Dialog close event fired');
      // Trigger scroll unlock
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    });
  }

  /**
   * Close the pickup modal
   */
  function closePickupModal(dialog) {
    if (!dialog) {
      dialog = document.querySelector('#global-pickup-drawer');
    }

    if (!dialog) {
      console.warn('[Global Store Selector] Dialog not found for closing');
      return;
    }

    // console.log('[Global Store Selector] Closing pickup modal');

    // Use PopupActions if available, otherwise use native methods
    if (dialog.popupActions && typeof dialog.popupActions.popupClose === 'function') {
      try {
        dialog.popupActions.popupClose();
      } catch (e) {
        console.warn('[Global Store Selector] Error using PopupActions.close, falling back to native close:', e);
        closeDialogNative(dialog);
      }
    } else {
      closeDialogNative(dialog);
    }
  }

  /**
   * Close dialog using native methods
   */
  function closeDialogNative(dialog) {
    // Close dialog
    if (typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }

    // Set inert attribute
    dialog.setAttribute('inert', '');

    // Trigger scroll unlock
    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));

    // Ensure drawer inner is hidden
    const drawerInner = dialog.querySelector('.drawer__inner');
    if (drawerInner) {
      drawerInner.style.visibility = 'hidden';
    }
  }

  /**
   * Get variant ID from cart (first item)
   */
  async function getVariantIdFromCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      if (cart.items && cart.items.length > 0) {
        return cart.items[0].variant_id.toString();
      }
    } catch (error) {
      console.error('[Global Store Selector] Error fetching cart:', error);
    }
    return null;
  }

  /**
   * Manually initialize search functionality if wrapper methods aren't available
   */
  function initializeSearchManually(wrapper) {
    const searchInput = wrapper.querySelector('#pickup-search');
    if (!searchInput) {
      console.warn('[Global Store Selector] Search input not found in wrapper');
      return;
    }
    
    // console.log('[Global Store Selector] Manually initializing search');
    
    // Set input attributes
    searchInput.setAttribute('inputmode', 'numeric');
    searchInput.setAttribute('pattern', '[0-9]*');
    
    let composing = false;
    
    // Input validation handlers (same as pickup-location.js)
    searchInput.addEventListener('beforeinput', function (e) {
      const data = e.data || '';
      if (e.inputType && (e.inputType.startsWith('insert'))) {
        if (/\D/.test(data)) {
          e.preventDefault();
        }
      }
    });
    
    searchInput.addEventListener('compositionstart', function () { composing = true; });
    searchInput.addEventListener('compositionend', function (e) {
      composing = false;
      if (/\D/.test(e.data || '')) {
        this.value = this.value.replace(/\D/g, '');
        this.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });
    
    searchInput.addEventListener('paste', function (e) {
      const paste = (e.clipboardData || window.clipboardData).getData('text') || '';
      if (/\D/.test(paste)) {
        e.preventDefault();
        const digits = paste.replace(/\D/g, '');
        const start = this.selectionStart || 0;
        const end = this.selectionEnd || 0;
        const newVal = this.value.slice(0, start) + digits + this.value.slice(end);
        this.value = newVal;
        const pos = start + digits.length;
        this.setSelectionRange(pos, pos);
        this.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });
    
    searchInput.addEventListener('keydown', function (e) {
      if (["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) {
        return;
      }
      if (composing) return;
      
      if (!/^[0-9]$/.test(e.key)) {
        e.preventDefault();
      }
    });
    
    // Search functionality
    searchInput.addEventListener('input', function (e) {
      const input = this;
      const sanitized = input.value.replace(/\D/g, '');
      if (input.value !== sanitized) {
        input.value = sanitized;
        input.setSelectionRange(sanitized.length, sanitized.length);
      }
      
      const keyword = input.value.trim();
      const pickupItems = Array.from(wrapper.querySelectorAll('.pickup__list__item'));
      
      if (!keyword) {
        pickupItems.forEach(item => item.style.display = '');
        return;
      }
      
      const keywordNum = parseInt(keyword, 10);
      const firstDigit = keyword.charAt(0);
      
      const matched = pickupItems.map(item => {
        const zipText = item.querySelector('.address-zipcode-location')?.innerText.trim() || '';
        const zipNum = parseInt(zipText, 10) || 0;
        
        return {
          item: item,
          zip: zipText,
          zipNum: zipNum,
          match: zipText.includes(keyword) || zipNum.toString().includes(keyword) || 
                 (zipNum > 0 && keywordNum > 0 && zipNum.toString().startsWith(firstDigit))
        };
      });
      
      matched.forEach(({ item, match }) => {
        item.style.display = match ? '' : 'none';
      });
    });
    
    // console.log('[Global Store Selector] Search initialized manually');
  }

  /**
   * Handle store selection when click-and-collect-wrapper is not available
   */
  function handleStoreSelection(item) {
    const storeName = item.querySelector('.address_name')?.innerText.trim() || '';
    const storeAddress = item.querySelector('.address_infor')?.innerText.trim() || '';
    const storePhone = item.querySelector('.phone a')?.innerText.trim() || '';
    
    if (storeName) {
      localStorage.setItem('selectedPickupStore', storeName);
      localStorage.setItem('selectedPickupAddress', storeAddress);
      localStorage.setItem('selectedPickupPhone', storePhone);
      
      // Update all cart items with the selected store
      updateAllCartItemsPickupStore(storeName);
      
      // Reset modal shown flag since store was selected
      resetModalShownFlag();
      
      // Dispatch event
      document.dispatchEvent(new CustomEvent('pickup:store:selected', {
        detail: { storeName: storeName }
      }));
      
      // Close dialog
      const dialog = item.closest('[data-pickup-drawer]');
      if (dialog && typeof dialog.close === 'function') {
        dialog.close();
      } else if (dialog) {
        dialog.removeAttribute('open');
      }
      
      // console.log('[Global Store Selector] Store selected:', storeName);
    }
  }

  /**
   * Ensure form has pickup store property
   */
  function ensureFormHasPickupStore(form) {
    const storeName = getSelectedStore();
    if (!storeName || !form) return;
    
    const hiddenInput = form.querySelector(`input[name="properties[${PROPERTY_KEY}]"]`);
    if (hiddenInput) {
      hiddenInput.value = storeName;
    } else {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${PROPERTY_KEY}]`;
      input.value = storeName;
      form.appendChild(input);
    }
  }

  /**
   * Intercept theme:cart:add event
   * This is dispatched before CartItems processes the add-to-cart
   */
  function interceptCartAddEvent() {
    document.addEventListener('theme:cart:add', function(e) {
      // console.log('[Global Store Selector] theme:cart:add event intercepted');
      
      const button = e.detail?.button;
      if (!button) {
        console.log('[Global Store Selector] No button in event detail');
        return;
      }
      
      const form = button.closest('form');
      if (!form) {
        console.log('[Global Store Selector] No form found for button');
        return;
      }
      
      const storeName = getSelectedStore();
      // console.log('[Global Store Selector] Current store:', storeName || 'NONE');
      
      // If no store selected, check if modal has been shown before
      if (!storeName) {
        const modalShown = hasModalBeenShown();
        
        // Only show modal on first attempt
        if (!modalShown) {
          const dialog = document.querySelector('[data-pickup-drawer]');
          
          // console.log('[Global Store Selector] No store selected, first attempt - showing modal');
          
          // Prevent the event from reaching CartItems
          e.stopImmediatePropagation();
          e.preventDefault();
          
          // Mark modal as shown
          markModalAsShown();
          
          // Show modal with form context
          const wrapper = document.querySelector('#global-pickup-wrapper');
          if (wrapper) {
            showPickupModal(form);
            
            let storeSelected = false;
            
            // Store button and form for retry when store is selected
            const retryAddToCartWithStore = function() {
              const newStoreName = getSelectedStore();
              if (newStoreName) {
                storeSelected = true;
                // console.log('[Global Store Selector] Store selected, retrying add-to-cart');
                document.removeEventListener('pickup:store:selected', retryAddToCartWithStore);
                
                // Reset modal shown flag since store was selected
                resetModalShownFlag();
                
                // Ensure form has pickup store property
                ensureFormHasPickupStore(form);
                
                // Retry by dispatching the event again
                setTimeout(() => {
                  document.dispatchEvent(new CustomEvent('theme:cart:add', {
                    detail: { button: button },
                    bubbles: false
                  }));
                }, 100);
              }
            };
            
            // Listen for store selection
            document.addEventListener('pickup:store:selected', retryAddToCartWithStore, { once: true });
            
            // Listen for modal close
            const dialog = wrapper.querySelector('[data-pickup-drawer]') || document.querySelector('#global-pickup-drawer');
            if (dialog) {
              const handleModalClose = function() {
                setTimeout(() => {
                  const stillNoStore = !getSelectedStore();
                  if (stillNoStore && !storeSelected) {
                    // console.log('[Global Store Selector] Modal closed without store selection - proceeding with add-to-cart (without pickup store property)');

                    // Remove the event listener for store selection since we're proceeding without it
                    document.removeEventListener('pickup:store:selected', retryAddToCartWithStore);

                    // Retry add-to-cart without the store property
                    // The modal shown flag is already set, so this will proceed normally
                    setTimeout(() => {
                      // Create a new event and dispatch it
                      // This will pass through the interceptor because modalShown flag is set
                      const addToCartEvent = new CustomEvent('theme:cart:add', {
                        detail: { button: button },
                        bubbles: false,
                        cancelable: true
                      });
                      document.dispatchEvent(addToCartEvent);
                    }, 100);
                  }
                }, 100);
              };

              dialog.addEventListener('close', handleModalClose, { once: true });
            }
          } else {
            console.error('[Global Store Selector] Global pickup wrapper not found! Cannot show pickup store selection.');
          }
          
          return; // Exit early, preventing add-to-cart
        } else {
          // Modal was already shown once, allow add-to-cart to proceed without store
          // console.log('[Global Store Selector] Modal already shown once, allowing add-to-cart without store');
          // Don't prevent the event - let it proceed normally
        }
      } else {
        // Store is selected - ensure form has pickup store property
        // console.log('[Global Store Selector] Store selected, ensuring form has pickup property');
        resetModalShownFlag(); // Reset flag when store is selected
        ensureFormHasPickupStore(form);
      }
    }, true); // Use capture phase to intercept before CartItems
  }

  /**
   * Intercept fetch calls to /cart/add.js (for direct fetch calls like build-your-routine)
   */
  function interceptFetchCalls() {
    const originalFetch = window.fetch;
    
    window.fetch = function(...args) {
      const url = args[0];
      
      // Check if this is a cart add request
      if (typeof url === 'string' && (url.includes('/cart/add.js') || url.includes('/cart/add'))) {
        const storeName = getSelectedStore();
        
        // If no store selected, check if modal has been shown before
        if (!storeName) {
          const modalShown = hasModalBeenShown();
          
          // Only prevent fetch on first attempt
          if (!modalShown) {
            const dialog = document.querySelector('[data-pickup-drawer]');
            if (dialog) {
              // console.log('[Global Store Selector] No store selected, first attempt - preventing fetch add-to-cart');
              
              // Mark modal as shown
              markModalAsShown();
              
              // Show modal
              showPickupModal();
              
              // Return a rejected promise to prevent the fetch
              return Promise.reject(new Error('Pickup store must be selected before adding to cart'));
            }
          } else {
            // Modal already shown once, allow fetch to proceed without store
            console.log('[Global Store Selector] Modal already shown once, allowing fetch add-to-cart without store');
          }
        }
        
        // If store is selected, modify request body to include pickup store
        if (storeName && args[1] && args[1].body) {
          try {
            if (args[1].body instanceof FormData) {
              // FormData body - add pickup store property
              args[1].body.set(`properties[${PROPERTY_KEY}]`, storeName);
            } else if (typeof args[1].body === 'string') {
              // JSON string body
              const payload = JSON.parse(args[1].body);
              
              // Handle both single item and multiple items format
              if (payload.items && Array.isArray(payload.items)) {
                payload.items = payload.items.map(item => {
                  if (!item.properties) {
                    item.properties = {};
                  }
                  item.properties[PROPERTY_KEY] = storeName;
                  return item;
                });
              } else {
                if (!payload.properties) {
                  payload.properties = {};
                }
                payload.properties[PROPERTY_KEY] = storeName;
              }
              
              args[1].body = JSON.stringify(payload);
            } else if (args[1].body && typeof args[1].body === 'object' && !(args[1].body instanceof FormData)) {
              // Object body (will be stringified)
              if (args[1].body.items && Array.isArray(args[1].body.items)) {
                args[1].body.items = args[1].body.items.map(item => {
                  if (!item.properties) {
                    item.properties = {};
                  }
                  item.properties[PROPERTY_KEY] = storeName;
                  return item;
                });
              } else {
                if (!args[1].body.properties) {
                  args[1].body.properties = {};
                }
                args[1].body.properties[PROPERTY_KEY] = storeName;
              }
            }
          } catch (error) {
            console.error('[Global Store Selector] Error modifying cart add request:', error);
          }
        }
      }
      
      return originalFetch.apply(this, args);
    };
  }

  /**
   * Update all cart items with the selected pickup store
   * Uses sequential updates to avoid race conditions (like pickup-location.js)
   */
  async function updateAllCartItemsPickupStore(storeName) {
    if (!storeName) return;
    
    // Prevent duplicate updates
    if (cartUpdateInProgress) {
      console.log('[Global Store Selector] Cart update already in progress, skipping');
      return;
    }
    
    // Skip if we just updated with the same store
    if (lastUpdatedStore === storeName) {
      console.log('[Global Store Selector] Already updated with this store, skipping');
      return;
    }
    
    cartUpdateInProgress = true;
    lastUpdatedStore = storeName;
    
    try {
      // console.log('[Global Store Selector] Updating all cart items with pickup store:', storeName);
      
      // Get current cart
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        console.error('[Global Store Selector] Failed to fetch cart');
        return;
      }
      
      const cart = await cartResponse.json();
      
      if (!cart.items || cart.items.length === 0) {
        console.log('[Global Store Selector] Cart is empty, nothing to update');
        return;
      }
      
      // Update each cart item sequentially (one at a time) to avoid race conditions
      // This matches the behavior of pickup-location.js
      for (const item of cart.items) {
        try {
          // Handle null/undefined properties
          const currentProperties = item.properties || {};
          
          // Only update if the property doesn't already match (avoid unnecessary updates)
          if (currentProperties[PROPERTY_KEY] === storeName) {
            // console.log(`[Global Store Selector] Item ${item.key} already has correct store, skipping`);
            continue;
          }
          
          const updatedProperties = {
            ...currentProperties,
            [PROPERTY_KEY]: storeName
          };
          
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: item.key,
              quantity: item.quantity,
              properties: updatedProperties
            })
          });
          
          if (!response.ok) {
            const errorText = await response.text();
            // console.error(`[Global Store Selector] Failed to update item ${item.key}:`, response.status, errorText);
            // Continue with next item instead of stopping
            continue;
          }
          
          // Small delay between updates to avoid overwhelming the server
          await new Promise(resolve => setTimeout(resolve, 50));
        } catch (error) {
          console.error(`[Global Store Selector] Error updating item ${item.key}:`, error);
          // Continue with next item
        }
      }
      
      // console.log('[Global Store Selector] Finished updating cart items');
      
      // Note: We intentionally do NOT trigger cart refresh events or call getCart()
      // to prevent the cart drawer from opening when updating pickup store properties.
      // The properties are stored server-side and will be correct on the next natural
      // cart refresh (when user adds item, opens cart drawer manually, etc.)
      // No need to refresh the cart UI immediately - it will be correct when viewed next.
    } catch (error) {
      console.error('[Global Store Selector] Error updating cart items:', error);
    } finally {
      // Reset flag after a short delay to allow for retries if needed
      setTimeout(() => {
        cartUpdateInProgress = false;
      }, 1000);
    }
  }

  /**
   * Listen for store selection events
   */
  function setupStoreSelectionListener() {
    // Listen for storage changes (when store is selected in another tab)
    window.addEventListener('storage', function(e) {
      if (e.key === STORAGE_KEY && e.newValue) {
        resetModalShownFlag(); // Reset flag when store is selected
        document.dispatchEvent(new CustomEvent('pickup:store:selected', {
          detail: { storeName: e.newValue }
        }));
      }
    });
    
    // Listen for pickup store selection events
    document.addEventListener('pickup:store:selected', function(e) {
      const storeName = e.detail?.storeName || getSelectedStore();
      if (storeName) {
        resetModalShownFlag(); // Reset flag when store is selected
        // console.log('[Global Store Selector] Store selected, updating all cart items');
        updateAllCartItemsPickupStore(storeName);
      }
    });
  }

  /**
   * Override CartItems.addToCart method as fallback
   * This ensures pickup store is added even if theme:cart:add event is bypassed
   */
  function interceptCartItemsAddToCart() {
    // Wait for CartItems to be defined
    const checkCartItems = setInterval(() => {
      if (window.customElements && window.customElements.get('cart-items')) {
        clearInterval(checkCartItems);
        
        const CartItemsElement = window.customElements.get('cart-items');
        const originalAddToCart = CartItemsElement.prototype.addToCart;
        
        CartItemsElement.prototype.addToCart = function(formData, button) {
          const storeName = getSelectedStore();
          
          // Add pickup store to form data if available
          if (storeName && formData instanceof FormData) {
            formData.set(`properties[${PROPERTY_KEY}]`, storeName);
          }
          
          // Call original method
          return originalAddToCart.call(this, formData, button);
        };
      }
    }, 100);
    
    // Clear interval after 10 seconds
    setTimeout(() => clearInterval(checkCartItems), 10000);
  }

  /**
   * Override pickup-location.js selectStore to dispatch our event
   */
  function setupPickupLocationIntegration() {
    const checkPickupWrapper = setInterval(() => {
      const wrapper = document.querySelector('click-and-collect-wrapper');
      if (wrapper && wrapper.selectStore) {
        clearInterval(checkPickupWrapper);
        
        const originalSelectStore = wrapper.selectStore.bind(wrapper);
        wrapper.selectStore = function(item) {
          originalSelectStore(item);
          // Dispatch event after store is selected
          setTimeout(() => {
            const storeName = getSelectedStore();
            if (storeName) {
              // Dispatch event which will trigger cart update
              document.dispatchEvent(new CustomEvent('pickup:store:selected', {
                detail: { storeName: storeName }
              }));
            }
          }, 100);
        };
      }
    }, 100);
    
    setTimeout(() => clearInterval(checkPickupWrapper), 10000);
  }

  /**
   * Check if global store selector is enabled
   */
  function isGlobalStoreSelectorEnabled() {
    // Check theme settings
    if (window.theme && window.theme.settings && window.theme.settings.globalStoreSelectorEnable !== undefined) {
      return window.theme.settings.globalStoreSelectorEnable === true;
    }
    
    // Default to enabled if setting not found
    return true;
  }

  // Check if feature is enabled before initializing
  if (!isGlobalStoreSelectorEnabled()) {
    // console.log('[Global Store Selector] Feature is disabled in theme settings');
    return;
  }

  // Initialize immediately
  interceptFetchCalls();
  interceptCartAddEvent();
  setupStoreSelectionListener();
  
  // Initialize when DOM is ready
  function initRest() {
    interceptCartItemsAddToCart();
    setupPickupLocationIntegration();
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRest);
  } else {
    initRest();
  }

  // console.log('[Global Store Selector] Initialized');

})();
